import { useState, useEffect } from 'react';
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
 User,
 Briefcase,
 Award,
 Plus,
 X,
 Edit3,
 CheckCircle,
 AlertCircle,
 MapPin,
 Mail,
 Phone,
} from 'lucide-react';
import { ImprovedOnboardingLayout } from '../ImprovedOnboardingLayout';
import type {
 ParsedResumeData,
 WorkExperience,
 Education,
 Certification,
 UserSkill,
} from '@/types/onboarding';

interface ProfileReviewStepProps {
 initialData?: ParsedResumeData;
 onDataUpdate: (data: Partial<ParsedResumeData>) => void;
 onNext: () => void;
 onPrevious: () => void;
}

export function ProfileReviewStep({
 initialData,
 onDataUpdate,
 onNext,
 onPrevious,
}: ProfileReviewStepProps) {
 const [profileData, setProfileData] = useState({
  personalInfo: {
   name: '',
   email: '',
   phone: '',
   location: '',
   linkedinUrl: '',
   githubUrl: '',
   portfolioUrl: '',
  },
  summary: '',
  industry: '',
  experienceLevel: 'mid' as string,
  currentRole: {
   title: '',
   company: '',
   description: '',
   isCurrentRole: true,
  },
  experience: [] as WorkExperience[],
  education: [] as Education[],
  certifications: [] as Certification[],
  skills: [] as UserSkill[],
  projects: [] as any[],
 });

 const [isEditing, setIsEditing] = useState({
  personalInfo: false,
  currentRole: false,
  experience: false,
  education: false,
  skills: false,
 });

 const [completeness, setCompleteness] = useState(0);

 // Initialize with extracted data
 useEffect(() => {
  if (initialData) {
   setProfileData({
    personalInfo: {
     name: initialData.personalInfo.name || '',
     email: initialData.personalInfo.email || '',
     phone: initialData.personalInfo.phone || '',
     location: initialData.personalInfo.location || '',
     linkedinUrl: initialData.personalInfo.linkedinUrl || '',
     githubUrl: initialData.personalInfo.githubUrl || '',
     portfolioUrl: initialData.personalInfo.portfolioUrl || '',
    },
    summary: initialData.summary || '',
    currentRole: initialData.currentRole
     ? {
        title: initialData.currentRole.title || '',
        company: initialData.currentRole.company || '',
        description: initialData.currentRole.description || '',
        isCurrentRole: initialData.currentRole.isCurrentRole ?? true,
       }
     : {
        title: '',
        company: '',
        description: '',
        isCurrentRole: true,
       },
    industry: initialData.industry || '',
    experienceLevel: initialData.experienceLevel || 'mid',
    experience: initialData.experience || [],
    education: initialData.education || [],
    certifications: initialData.certifications || [],
    skills: initialData.skills || [],
    projects: initialData.projects || [],
   });
  }
 }, [initialData]);

 // Calculate profile completeness
 useEffect(() => {
  const requiredFields = [
   profileData.personalInfo.name,
   profileData.personalInfo.email,
   profileData.currentRole.title,
   profileData.currentRole.company,
  ];

  const optionalFields = [
   profileData.personalInfo.phone,
   profileData.personalInfo.location,
   profileData.summary,
   profileData.currentRole.description,
  ];

  const requiredComplete = requiredFields.filter(
   (field) => field && field.trim() !== ''
  ).length;
  const optionalComplete = optionalFields.filter(
   (field) => field && field.trim() !== ''
  ).length;
  const skillsComplete = profileData.skills.length > 0 ? 1 : 0;
  const experienceComplete = profileData.experience.length > 0 ? 1 : 0;

  const totalComplete =
   requiredComplete + optionalComplete + skillsComplete + experienceComplete;
  const totalPossible = requiredFields.length + optionalFields.length + 2; // +2 for skills and experience

  setCompleteness(Math.round((totalComplete / totalPossible) * 100));
 }, [profileData]);

 const handleContinue = () => {
  onDataUpdate(profileData);
  onNext();
 };

 // Unused state updaters removed for brevity

 const addSkill = () => {
  setProfileData((prev) => ({
   ...prev,
   skills: [
    ...prev.skills,
    {
     skillId: `custom_${Date.now()}`,
     name: '',
     proficiencyLevel: 'intermediate' as const,
     isVerified: false,
    },
   ],
  }));
 };

 const removeSkill = (index: number) => {
  setProfileData((prev) => ({
   ...prev,
   skills: prev.skills.filter((_, i) => i !== index),
  }));
 };

 const updateSkill = (index: number, field: string, value: any) => {
  setProfileData((prev) => ({
   ...prev,
   skills: prev.skills.map((skill, i) =>
    i === index ? { ...skill, [field]: value } : skill
   ),
  }));
 };

 const isProfileComplete = completeness >= 70; // Require at least 70% completion

 return (
  <ImprovedOnboardingLayout
   currentStep={2}
   totalSteps={4}
   onNext={isProfileComplete ? handleContinue : undefined}
   onPrevious={onPrevious}
   isNextDisabled={!isProfileComplete}
   nextButtonText='Continue'
  >
   <div className='space-y-6'>
    {/* Profile Completeness */}
    <Card className='rounded-none border-primary/20 bg-secondary/5'>
     <CardHeader className='pb-2'>
      <div className='flex items-center justify-between'>
       <div>
        <CardTitle className='flex items-center gap-2 font-mono text-base uppercase tracking-wider text-primary'>
         <CheckCircle className='h-4 w-4' />
         Profile_Status
        </CardTitle>
        <CardDescription className='font-mono text-xs mt-1'>
         &gt; verifying_completeness_metrics...
        </CardDescription>
       </div>
       <div className='text-right'>
        <div className='font-mono text-2xl font-bold text-primary'>
         {completeness}%
        </div>
        <div className='font-mono text-[10px] text-muted-foreground uppercase tracking-widest'>
         CALCULATED
        </div>
       </div>
      </div>
     </CardHeader>
     <CardContent>
      <div className='w-full bg-secondary/50 h-2 mb-2'>
       <div
        className='bg-primary h-full transition-all duration-500'
        style={{ width: `${completeness}%` }}
       />
      </div>
      {completeness < 70 && (
       <div className='mt-4 p-3 border-l-2 border-yellow-500 bg-yellow-500/10'>
        <div className='flex items-start gap-3'>
         <AlertCircle className='h-4 w-4 text-yellow-500 mt-0.5' />
         <div className='font-mono text-xs text-yellow-500'>
          WARNING: INSUFFICIENT_DATA
          <br />
          Missing required fields marked with [*].
         </div>
        </div>
       </div>
      )}
     </CardContent>
    </Card>

    {/* Personal Information */}
    <Card className='rounded-none border-border bg-card'>
     <CardHeader>
      <div className='flex items-center justify-between'>
       <div>
        <CardTitle className='flex items-center gap-2 font-mono text-lg uppercase tracking-wider'>
         <User className='h-5 w-5 text-primary' />
         Personal_Data
        </CardTitle>
        <CardDescription className='font-mono text-xs'>
         &gt; user_identification_records
        </CardDescription>
       </div>
       <Button
        variant='ghost'
        size='sm'
        onClick={() =>
         setIsEditing((prev) => ({ ...prev, personalInfo: !prev.personalInfo }))
        }
        className='font-mono text-xs rounded-none hover:bg-primary/10 hover:text-primary'
       >
        <Edit3 className='h-3 w-3 mr-2' />
        {isEditing.personalInfo ? 'SAVE_CHANGES' : 'EDIT_DATA'}
       </Button>
      </div>
     </CardHeader>
     <CardContent>
      {isEditing.personalInfo ? (
       <div className='grid gap-6'>
        <div className='grid md:grid-cols-2 gap-4'>
         <div className='space-y-2'>
          <Label
           htmlFor='name'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Full Name *
          </Label>
          <Input
           id='name'
           value={profileData.personalInfo.name}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: { ...prev.personalInfo, name: e.target.value },
            }))
           }
           placeholder='JOHN DOE'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
         <div className='space-y-2'>
          <Label
           htmlFor='email'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Email Address *
          </Label>
          <Input
           id='email'
           type='email'
           value={profileData.personalInfo.email}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: { ...prev.personalInfo, email: e.target.value },
            }))
           }
           placeholder='USER@EXAMPLE.COM'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
         <div className='space-y-2'>
          <Label
           htmlFor='phone'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Phone Number
          </Label>
          <Input
           id='phone'
           value={profileData.personalInfo.phone}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: { ...prev.personalInfo, phone: e.target.value },
            }))
           }
           placeholder='+1 (555) 000-0000'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
         <div className='space-y-2'>
          <Label
           htmlFor='location'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Location
          </Label>
          <Input
           id='location'
           value={profileData.personalInfo.location}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: { ...prev.personalInfo, location: e.target.value },
            }))
           }
           placeholder='CITY, COUNTRY'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
        </div>
        <div className='grid md:grid-cols-3 gap-4'>
         <div className='space-y-2'>
          <Label
           htmlFor='linkedin'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           LinkedIn URL
          </Label>
          <Input
           id='linkedin'
           value={profileData.personalInfo.linkedinUrl}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: {
              ...prev.personalInfo,
              linkedinUrl: e.target.value,
             },
            }))
           }
           placeholder='LINKEDIN/IN/USER'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
         <div className='space-y-2'>
          <Label
           htmlFor='github'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           GitHub URL
          </Label>
          <Input
           id='github'
           value={profileData.personalInfo.githubUrl}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: { ...prev.personalInfo, githubUrl: e.target.value },
            }))
           }
           placeholder='GITHUB.COM/USER'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
         <div className='space-y-2'>
          <Label
           htmlFor='portfolio'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Portfolio URL
          </Label>
          <Input
           id='portfolio'
           value={profileData.personalInfo.portfolioUrl}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             personalInfo: {
              ...prev.personalInfo,
              portfolioUrl: e.target.value,
             },
            }))
           }
           placeholder='USER.DEV'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
        </div>
       </div>
      ) : (
       <div className='grid gap-4 p-4 border border-dashed border-border/50 bg-secondary/5 font-mono text-sm'>
        <div className='flex items-center gap-3'>
         <User className='h-4 w-4 text-primary opacity-70' />
         <span className='w-24 text-muted-foreground text-xs uppercase'>
          NAME:
         </span>
         <span className='font-semibold font-mono'>
          {profileData.personalInfo.name || 'N/A'}
         </span>
        </div>
        <div className='flex items-center gap-3'>
         <Mail className='h-4 w-4 text-primary opacity-70' />
         <span className='w-24 text-muted-foreground text-xs uppercase'>
          EMAIL:
         </span>
         <span className='font-mono flex-1 break-all'>
          {profileData.personalInfo.email || 'N/A'}
         </span>
        </div>
        {profileData.personalInfo.phone && (
         <div className='flex items-center gap-3'>
          <Phone className='h-4 w-4 text-primary opacity-70' />
          <span className='w-24 text-muted-foreground text-xs uppercase'>
           PHONE:
          </span>
          <span className='font-mono'>{profileData.personalInfo.phone}</span>
         </div>
        )}
        {profileData.personalInfo.location && (
         <div className='flex items-center gap-3'>
          <MapPin className='h-4 w-4 text-primary opacity-70' />
          <span className='w-24 text-muted-foreground text-xs uppercase'>
           LOC:
          </span>
          <span>{profileData.personalInfo.location}</span>
         </div>
        )}
       </div>
      )}
     </CardContent>
    </Card>

    {/* Current Role */}
    <Card className='rounded-none border-border bg-card'>
     <CardHeader>
      <div className='flex items-center justify-between'>
       <div>
        <CardTitle className='flex items-center gap-2 font-mono text-lg uppercase tracking-wider'>
         <Briefcase className='h-5 w-5 text-primary' />
         Current_Role
        </CardTitle>
        <CardDescription className='font-mono text-xs'>
         &gt; employment_status_verification
        </CardDescription>
       </div>
       <Button
        variant='ghost'
        size='sm'
        onClick={() =>
         setIsEditing((prev) => ({ ...prev, currentRole: !prev.currentRole }))
        }
        className='font-mono text-xs rounded-none hover:bg-primary/10 hover:text-primary'
       >
        <Edit3 className='h-3 w-3 mr-2' />
        {isEditing.currentRole ? 'SAVE_CHANGES' : 'EDIT_DATA'}
       </Button>
      </div>
     </CardHeader>
     <CardContent>
      {isEditing.currentRole ? (
       <div className='space-y-4'>
        <div className='grid md:grid-cols-2 gap-4'>
         <div className='space-y-2'>
          <Label
           htmlFor='currentTitle'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Job Title *
          </Label>
          <Input
           id='currentTitle'
           value={profileData.currentRole.title}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             currentRole: { ...prev.currentRole, title: e.target.value },
            }))
           }
           placeholder='SOFTWARE ENGINEER'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
         <div className='space-y-2'>
          <Label
           htmlFor='currentCompany'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Company *
          </Label>
          <Input
           id='currentCompany'
           value={profileData.currentRole.company}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             currentRole: { ...prev.currentRole, company: e.target.value },
            }))
           }
           placeholder='TECH CORP'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
         <div className='space-y-2'>
          <Label
           htmlFor='industry'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Industry
          </Label>
          <Input
           id='industry'
           value={profileData.industry}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             industry: e.target.value,
            }))
           }
           placeholder='SOFTWARE / FINTECH'
           className='font-mono rounded-none bg-background border-input focus:border-primary'
          />
         </div>
         <div className='space-y-2'>
          <Label
           htmlFor='experienceLevel'
           className='font-mono text-xs uppercase text-muted-foreground'
          >
           Experience Level
          </Label>
          <select
           id='experienceLevel'
           value={profileData.experienceLevel}
           onChange={(e) =>
            setProfileData((prev) => ({
             ...prev,
             experienceLevel: e.target.value,
            }))
           }
           className='w-full px-3 py-2 border border-input bg-background font-mono text-xs h-10 rounded-none'
          >
           <option value='junior'>JUNIOR</option>
           <option value='mid'>MID_LEVEL</option>
           <option value='senior'>SENIOR</option>
           <option value='lead'>LEAD</option>
           <option value='principal'>PRINCIPAL</option>
           <option value='executive'>EXECUTIVE</option>
          </select>
         </div>
        </div>
        <div className='space-y-2'>
         <Label
          htmlFor='currentDescription'
          className='font-mono text-xs uppercase text-muted-foreground'
         >
          Role Description
         </Label>
         <Textarea
          id='currentDescription'
          value={profileData.currentRole.description}
          onChange={(e) =>
           setProfileData((prev) => ({
            ...prev,
            currentRole: { ...prev.currentRole, description: e.target.value },
           }))
          }
          placeholder='BRIEF DESCRIPTION OF YOUR CURRENT ROLE...'
          rows={3}
          className='font-mono rounded-none bg-background border-input focus:border-primary text-sm'
         />
        </div>
       </div>
      ) : (
       <div className='space-y-2 p-4 border border-dashed border-border/50 bg-secondary/5 font-mono text-sm'>
        <div className='flex justify-between items-start'>
         <div>
          <h4 className='font-bold text-base text-primary font-mono'>
           {profileData.currentRole.title
            ? profileData.currentRole.title.toUpperCase()
            : 'TITLE NOT PROVIDED'}
          </h4>
          <p className='text-muted-foreground text-xs uppercase tracking-wider mt-1 font-mono'>
           AT{' '}
           {profileData.currentRole.company
            ? profileData.currentRole.company.toUpperCase()
            : 'COMPANY NOT PROVIDED'}
          </p>
          <div className='flex gap-2 mt-2'>
           {profileData.industry && (
            <Badge
             variant='outline'
             className='rounded-none font-mono text-[10px] bg-secondary/30 border-border'
            >
             {profileData.industry.toUpperCase()}
            </Badge>
           )}
           {profileData.experienceLevel && (
            <Badge
             variant='outline'
             className='rounded-none font-mono text-[10px] bg-secondary/30 border-border'
            >
             {profileData.experienceLevel.toUpperCase()}
            </Badge>
           )}
          </div>
         </div>
         <Badge
          variant='outline'
          className='rounded-none font-mono text-[10px] bg-primary/10 border-primary/20 text-primary'
         >
          CURRENT
         </Badge>
        </div>
        {profileData.currentRole.description && (
         <p className='text-xs text-muted-foreground mt-4 border-t border-border/50 pt-2 font-mono'>
          {profileData.currentRole.description}
         </p>
        )}
       </div>
      )}
     </CardContent>
    </Card>

    {/* Skills */}
    <Card className='rounded-none border-border bg-card'>
     <CardHeader>
      <div className='flex items-center justify-between'>
       <div>
        <CardTitle className='flex items-center gap-2 font-mono text-lg uppercase tracking-wider'>
         <Award className='h-5 w-5 text-primary' />
         Skill_Matrix ({profileData.skills.length})
        </CardTitle>
        <CardDescription className='font-mono text-xs'>
         &gt; competency_assessment_entries
        </CardDescription>
       </div>
       <Button
        variant='ghost'
        size='sm'
        onClick={() =>
         setIsEditing((prev) => ({ ...prev, skills: !prev.skills }))
        }
        className='font-mono text-xs rounded-none hover:bg-primary/10 hover:text-primary'
       >
        <Edit3 className='h-3 w-3 mr-2' />
        {isEditing.skills ? 'SAVE_CHANGES' : 'EDIT_DATA'}
       </Button>
      </div>
     </CardHeader>
     <CardContent>
      {isEditing.skills ? (
       <div className='space-y-4'>
        {profileData.skills.map((skill, index) => (
         <div key={index} className='flex gap-2 items-end'>
          <div className='flex-1 space-y-2'>
           <Label className='font-mono text-xs uppercase text-muted-foreground'>
            Skill Name
           </Label>
           <Input
            value={skill.name}
            onChange={(e) => updateSkill(index, 'name', e.target.value)}
            placeholder='JAVASCRIPT'
            className='font-mono rounded-none h-9 text-sm'
           />
          </div>
          <div className='w-32 space-y-2'>
           <Label className='font-mono text-xs uppercase text-muted-foreground'>
            Level
           </Label>
           <select
            value={skill.proficiencyLevel}
            onChange={(e) =>
             updateSkill(index, 'proficiencyLevel', e.target.value)
            }
            className='w-full px-3 py-2 border border-input bg-background font-mono text-xs h-9'
           >
            <option value='beginner'>BEGINNER</option>
            <option value='intermediate'>INTERMEDIATE</option>
            <option value='advanced'>ADVANCED</option>
            <option value='expert'>EXPERT</option>
           </select>
          </div>
          <Button
           type='button'
           variant='outline'
           size='icon'
           onClick={() => removeSkill(index)}
           className='rounded-none border-destructive/50 text-destructive hover:bg-destructive/10'
          >
           <X className='h-4 w-4' />
          </Button>
         </div>
        ))}
        <Button
         type='button'
         variant='outline'
         onClick={addSkill}
         className='w-full rounded-none border-dashed border-primary/30 text-primary hover:bg-primary/5 font-mono text-xs uppercase'
        >
         <Plus className='h-4 w-4 mr-2' />
         Add_New_Skill_Entry
        </Button>
       </div>
      ) : (
       <div className='flex flex-wrap gap-2'>
        {profileData.skills.length > 0 ? (
         profileData.skills.map((skill, index) => (
          <Badge
           key={index}
           variant='outline'
           className='rounded-none font-mono text-xs border-primary/30 bg-primary/5 text-foreground py-1.5 px-3'
          >
           {skill.name.toUpperCase()}
           <span className='ml-2 text-primary opacity-70 border-l border-primary/20 pl-2'>
            {skill.proficiencyLevel.toUpperCase()}
           </span>
          </Badge>
         ))
        ) : (
         <p className='font-mono text-sm text-muted-foreground italic'>
          &gt; no_skills_recorded
         </p>
        )}
       </div>
      )}
     </CardContent>
    </Card>

    {/* Work Experience */}
    {profileData.experience.length > 0 && (
     <Card className='rounded-none border-border bg-card'>
      <CardHeader>
       <CardTitle className='flex items-center gap-2 font-mono text-lg uppercase tracking-wider'>
        <Briefcase className='h-5 w-5 text-primary' />
        Employment_History ({profileData.experience.length})
       </CardTitle>
      </CardHeader>
      <CardContent>
       <div className='space-y-4 font-mono'>
        {profileData.experience.map((exp, index) => (
         <div
          key={index}
          className='border-l-2 border-primary/20 pl-4 py-2 hover:bg-secondary/5 transition-colors'
         >
          <div className='flex items-start justify-between'>
           <div className='flex-1'>
            <h4 className='font-bold text-base text-primary uppercase'>
             {exp.title}
            </h4>
            <p className='text-xs uppercase text-muted-foreground mt-1'>
             AT {exp.company}
            </p>
            {exp.description && (
             <p className='text-xs text-foreground/80 mt-2 border-l border-border/50 pl-2'>
              {exp.description}
             </p>
            )}
            {exp.technologies.length > 0 && (
             <div className='flex flex-wrap gap-1 mt-3'>
              {exp.technologies.map((tech, techIndex) => (
               <Badge
                key={techIndex}
                variant='outline'
                className='rounded-none text-[10px] border-primary/20 bg-primary/5 text-primary'
               >
                {tech.toUpperCase()}
               </Badge>
              ))}
             </div>
            )}
           </div>
          </div>
         </div>
        ))}
       </div>
      </CardContent>
     </Card>
    )}
   </div>
  </ImprovedOnboardingLayout>
 );
}
